import * as React from 'react';
import { getCategoryUrlWithParams } from '@/utils/urls';
import { getSaveSearchSuccess } from '@/redux/modules/saveSearch';
import { modifyParamsRemoveUnfollowSearch, parseQueryParams, stringifyQuery } from '@/utils/queryParams';
import { submitDeleteSavedSearchUrl } from '@/redux/modules/savedSearch';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation, useNavigate } from 'react-router-dom';

export enum UnfollowSearchTypes {
    category = 'category',
    search = 'search',
}

export interface UnfollowSearchProps {
    searchType: UnfollowSearchTypes;
}

const UnfollowSearch = ({ searchType }: UnfollowSearchProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const { formatMessage } = useIntl();

    const queryParams = parseQueryParams(search);
    const { savedSearchId, signature } = queryParams;
    const newParams = modifyParamsRemoveUnfollowSearch(queryParams);
    const success = useAppSelector(getSaveSearchSuccess);

    const navigateSearchPage = React.useCallback(() => {
        navigate({
            search: stringifyQuery(newParams),
        });
    }, [navigate, newParams]);

    const navigateCategoryPage = React.useCallback(() => {
        navigate(getCategoryUrlWithParams(pathname, newParams));
    }, [pathname, navigate, newParams]);

    React.useEffect(() => {
        const unsub = async () => {
            await dispatch(submitDeleteSavedSearchUrl({ savedSearchId, signature }));
        };
        if (savedSearchId && signature) {
            unsub();
        }
    }, [dispatch, savedSearchId, searchType, signature]);

    React.useEffect(() => {
        if (success && savedSearchId) {
            toast('success', {
                description: formatMessage({ id: 'unfollowSearch' }),
                title: formatMessage({ id: 'toastMessages.titles.success' }),
            });
            if (searchType === UnfollowSearchTypes.search) {
                navigateSearchPage();
            } else {
                navigateCategoryPage();
            }
        }
        // leaving out navigation routines to prevent running twice after query params change
        // eslint-disable-next-line
    }, [searchType, savedSearchId, success, formatMessage]);

    return null;
};

export default UnfollowSearch;
